import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import type { InspirationData } from './types';

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

interface StoryControlsProps {
  inspiration: InspirationData;
  contentRef: React.RefObject<HTMLDivElement | null>;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
}

export function StoryControls({
  inspiration,
  contentRef,
  isProcessing,
  setIsProcessing,
}: StoryControlsProps) {
  const { t } = useTranslation();

  const captureImage = useCallback(async (): Promise<Blob | null> => {
    if (!contentRef.current) return null;

    try {
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#0f172a',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 1.0);
      });
    } catch {
      return null;
    }
  }, [contentRef]);

  const handleDownload = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const blob = await captureImage();
      if (!blob) throw new Error('Failed to capture');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yim-inspiration.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Download failed silently
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, captureImage, setIsProcessing]);

  const handleWhatsAppShare = useCallback(() => {
    if (isProcessing || !inspiration) return;

    const shareText = `"${inspiration.content}"${inspiration.reference ? `\n\n— ${inspiration.reference}` : ''}`;
    const encodedText = encodeURIComponent(shareText);

    const whatsappWindow = window.open(`https://wa.me/?text=${encodedText}`, '_blank');

    setIsProcessing(true);

    captureImage().then((blob) => {
      if (blob) {
        const file = new File([blob], 'yim-inspiration.png', { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          if (whatsappWindow) whatsappWindow.close();

          navigator.share({
            files: [file],
            text: shareText,
          }).catch(() => {
            downloadBlob(blob);
          });
        } else {
          downloadBlob(blob);
        }
      }
      setIsProcessing(false);
    }).catch(() => {
      setIsProcessing(false);
    });

    function downloadBlob(blob: Blob) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'yim-inspiration.png';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }
  }, [isProcessing, captureImage, inspiration, setIsProcessing]);

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={handleDownload}
        disabled={isProcessing}
        className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-colors bg-white/10 text-white border border-white/20"
      >
        {isProcessing ? (
          <Loader2 className="w-4 h-4 animate-spin text-white" />
        ) : (
          <Download className="w-4 h-4 text-white" />
        )}
        <span className="text-sm text-white">{t('components.inspiration.download')}</span>
      </button>

      <button
        onClick={handleWhatsAppShare}
        disabled={isProcessing}
        className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium shadow-lg transition-colors bg-[#25D366] text-white"
      >
        {isProcessing ? (
          <Loader2 className="w-4 h-4 animate-spin text-white" />
        ) : (
          <WhatsAppIcon className="w-4 h-4" />
        )}
        <span className="text-sm text-white">{t('components.inspiration.shareWhatsApp')}</span>
      </button>
    </div>
  );
}
